import { Link } from "react-router-dom";
import {
  Ship,
  Globe2,
  FileText,
  Tags,
  Radar,
  Package,
  Plane,
  ShieldCheck,
  Languages,
  Home,
  ArrowRight,
} from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import Technology from "@/components/site/Technology";

const HERO_IMG =
  "https://images.unsplash.com/photo-1769144256207-bc4bb75b29db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXBwaW5nJTIwcG9ydCUyMGNvbnRhaW5lciUyMGFlcmlhbHxlbnwwfHx8fDE3NzgwMzkxMDJ8MA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  {
    n: "01",
    icon: Ship,
    title: "Customized Exporting",
    lead: "Door-to-door delivery, designed around your cargo.",
    bullets: [
      "Cargo-specific routing and freight mode (sea / air / rail)",
      "Volume-smart consolidation to lower per-unit cost",
      "One contract covers pickup → final destination",
    ],
  },
  {
    n: "02",
    icon: Home,
    title: "Turnkey Furnishing & Building",
    lead: "Complete design → drawing → sourcing → delivery for hotels, offices, residences and commercial fit-outs.",
    bullets: [
      "Interior design, technical drawings and 3D visualisation",
      "Furniture, fixtures, lighting and decor — primary hub Yiwu; specialist sourcing from Foshan & Guangzhou",
      "Building materials, tiles, sanitary ware, cladding and hardware",
      "Project management, QC inspection and door-to-door install-ready delivery",
    ],
  },
  {
    n: "03",
    icon: Globe2,
    title: "Global Logistics",
    lead: "A worldwide network coordinated from Yiwu.",
    bullets: [
      "Trade lanes into India, UK, Middle East, EU, SE Asia",
      "Live tracking from origin warehouse to your door",
      "Risk-of-delay alerts before they hit your operations",
    ],
  },
  {
    n: "04",
    icon: FileText,
    title: "Expert Documentation",
    lead: "Customs paperwork done right, the first time.",
    bullets: [
      "HS code classification and duty estimation",
      "Origin certificates, BIS / FDA / CE wherever required",
      "Pre-cleared paperwork = faster port-side release",
    ],
  },
  {
    n: "05",
    icon: Tags,
    title: "Competitive Pricing",
    lead: "Transparent, volume-smart pricing at any scale.",
    bullets: [
      "Direct factory pricing — no hidden margins",
      "Quarterly rate reviews on repeat lanes",
      "FOB / CIF / DDP options to match your accounting",
    ],
  },
  {
    n: "06",
    icon: Radar,
    title: "Real-time Tracking",
    lead: "Always know where your container is.",
    bullets: [
      "24/7 visibility on every shipment, every leg",
      "Single dashboard across multiple SKUs",
      "ETA updates pushed to your inbox or WhatsApp",
    ],
  },
  {
    n: "07",
    icon: Package,
    title: "Expert Packaging",
    lead: "Protective, regulation-ready packaging.",
    bullets: [
      "Mode-appropriate (sea / air / rail) packing standards",
      "Fragile, hazardous and bulk consolidation handled",
      "Branding-friendly retail-ready packaging when needed",
    ],
  },
  {
    n: "08",
    icon: Plane,
    title: "Travel Assistance",
    lead: "Sourcing trip, fully arranged.",
    bullets: [
      "Visa invitation letters for China",
      "Flight + hotel booking near Yiwu",
      "Translator + chauffeur for market visits",
    ],
  },
  {
    n: "09",
    icon: ShieldCheck,
    title: "Customs & Door Delivery",
    lead: "Clearance in India, UK, Middle East and beyond.",
    bullets: [
      "Pre-cleared paperwork, port-side handling",
      "Last-mile delivery to your warehouse / store",
      "Refunds-friendly damage and shortage protocol",
    ],
  },
  {
    n: "10",
    icon: Languages,
    title: "Multilingual Sourcing",
    lead: "We walk the Yiwu market with you.",
    bullets: [
      "Mandarin / Hindi / English / Arabic on the floor",
      "Supplier shortlisting + factory audits before you fly",
      "Real-time negotiation, not WhatsApp ping-pong",
    ],
  },
];

const PROCESS = [
  { n: "01", title: "Brief", body: "You share product, volume, target landed price and destination." },
  { n: "02", title: "Source", body: "We shortlist factories, audit samples, lock pricing in Yiwu." },
  { n: "03", title: "Pack & ship", body: "We package, document and ship — sea, air or rail — with live tracking." },
  { n: "04", title: "Clear & deliver", body: "We handle destination customs and last-mile to your door." },
];

export default function ServicesPage() {
  return (
    <main data-testid="services-page" className="bg-ink-50">
      <Navigation />

      {/* Hero */}
      <section
        data-testid="services-hero"
        className="relative min-h-[68vh] flex items-end overflow-hidden bg-ink text-white pt-32 pb-16"
      >
        <img src={HERO_IMG} alt="Container ship" className="absolute inset-0 w-full h-full object-cover opacity-65" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/55 to-ink" />
        <div className="absolute inset-0 grain opacity-20" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-6">
            Services · 10 capabilities
          </p>
          <h1 className="font-heading font-black tracking-[-0.035em] leading-[0.9] text-5xl md:text-7xl lg:text-[110px] uppercase">
            One operator.<br />
            <span className="text-stroke-light">Whole chain.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-base md:text-lg leading-relaxed">
            From the factory floor in Yiwu to your warehouse door, every step is
            run by Emporia. Pick a service, or let us design the full route.
          </p>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-white py-14 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-6 md:space-y-8">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.n}
                data-testid={`service-detail-${s.n}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 p-8 md:p-12 bg-ink-50 border border-ink-100 hover:border-signal hover:-translate-y-1 transition-all duration-300"
              >
                <div className="md:col-span-3 flex md:flex-col items-start justify-between gap-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal">
                    {s.n}
                  </span>
                  <span className="w-14 h-14 flex items-center justify-center border border-ink-900 text-ink-900 group-hover:bg-ink-900 group-hover:text-white transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.6} />
                  </span>
                </div>
                <div className="md:col-span-5">
                  <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-3xl md:text-4xl">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-ink-500 text-base md:text-lg leading-relaxed">
                    {s.lead}
                  </p>
                </div>
                <ul className="md:col-span-4 space-y-3 md:border-l md:border-ink-100 md:pl-8">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-3 text-ink-800 text-[15px] leading-relaxed">
                      <span className="mt-2 inline-block w-2 h-2 bg-signal flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </section>

      {/* In-house tech */}
      <Technology variant="light" />

      {/* How we work */}
      <section className="bg-ink text-white py-14 md:py-28 relative overflow-hidden">        <div className="absolute inset-0 grain opacity-30" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-14 md:mb-20">
            <div className="md:col-span-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
                Process
              </p>
              <h2 className="font-heading font-black tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl lg:text-6xl">
                How we work.
              </h2>
            </div>
            <p className="md:col-span-5 md:col-start-8 text-white/70 text-base md:text-lg leading-relaxed">
              No black boxes. Four steps, one operator, weekly status updates.
              Most clients have a quote in 48 hours and goods in transit in 2–3
              weeks.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-5 md:gap-6">
            {PROCESS.map((p, i) => (
              <div
                key={p.n}
                data-testid={`process-step-${p.n}`}
                className="bg-ink-800/40 border border-white/10 p-7 md:p-8 hover:border-signal/70 transition-colors"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal">
                  Step {p.n}
                </span>
                <h3 className="mt-5 font-heading font-bold text-2xl md:text-[26px] tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-3 text-white/70 text-[15px] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-14 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-signal mb-5">
              Ready to ship?
            </p>
            <h2 className="font-heading font-black text-ink-900 tracking-[-0.03em] leading-[0.95] text-4xl md:text-5xl lg:text-6xl">
              Tell us your brief.<br />
              <span className="text-stroke">We'll come back with a route.</span>
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/#contact"
              data-testid="services-cta-contact"
              className="group inline-flex items-center gap-3 bg-signal hover:bg-signal-600 text-white px-7 py-4 font-mono text-[12px] uppercase tracking-[0.22em] transition-colors"
            >
              Get a Quote
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
